import { ActionContext } from 'vuex'
import apiAuth from '@/api/auth'
import util from '@/utils/classToObject'
import types from '@/store/all-mutation-types'
import { IRootState, loadInitalData } from '@/store'
import { IAuthState } from './state'
import { AlqIdentityToken } from '@/models/query/AlqIdentityToken'
import { ApiResultCode } from '@/models/response/AlrBase'
import { AloError } from '@/models/alo/AloError'
import { AloIdentityToken } from '@/models/alo/AloIdentityToken'
import { i18n } from '@/locale/vueI18n'
import { AlqForgottenPass } from '@/models/query/AlqForgottenPass'

export class AuthAction {
  public [types.AUTH_REQUEST](context: ActionContext<IAuthState, IRootState>, user: AlqIdentityToken) {
    return new Promise((resolve, reject) => {
      context.commit(types.AUTH_REQUEST)
      apiAuth
        .authenticate(user)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const identityToken = resp.data.data as AloIdentityToken
            const token = resp.headers.authorization
            //lance le renouvelement automatique
            context.dispatch(types.AUTH_AUTO_RENEW, (identityToken!.aloJwt.renewInSeconds - 10) * 1000)
            context.commit(types.AUTH_SUCCESS)
            context.commit(types.AUTH_USER_SUCCESS, {
              userProfile: identityToken!.aloUser,
              wsUrl: identityToken.wsUrl,
              token,
            })
            loadInitalData().finally(() => {
              resolve(resp.data.data)
            })
          } else {
            context.commit(types.AUTH_LOGOUT)
            context.commit(types.AUTH_ERROR)
            reject((resp.data.data as AloError).errorMessage)
          }
        })
        .catch(err => {
          context.commit(types.AUTH_LOGOUT)
          context.commit(types.AUTH_ERROR)
          reject(err)
        })
    })
  }
  public [types.AUTH_LOGOUT](context: ActionContext<IAuthState, IRootState>) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.AUTH_LOGOUT)
      resolve()
    })
  }

  public [types.AUTH_USER_REQUEST](context: ActionContext<IAuthState, IRootState>) {
    return new Promise((resolve, reject) => {
      context.commit(types.AUTH_USER_REQUEST)
      apiAuth
        .getAuthenticatedUser()
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const identityToken = resp.data.data as AloIdentityToken
            const token = resp.headers.authorization
            context.commit(types.AUTH_USER_SUCCESS, {
              userProfile: identityToken.aloUser,
              wsUrl: identityToken.wsUrl,
              token,
            })
            //lance le renouvelement automatique
            context.dispatch(types.AUTH_AUTO_RENEW, (identityToken.aloJwt.renewInSeconds - 10) * 1000)
            loadInitalData().finally(() => {
              resolve(resp.data.data)
            })
          } else {
            context.commit(types.AUTH_USER_ERROR)
            context.commit(types.AUTH_LOGOUT)
            reject((resp.data.data as AloError).errorMessage)
          }
        })
        .catch(err => {
          //console.log(err)
          context.commit(types.AUTH_USER_ERROR)
          context.commit(types.AUTH_LOGOUT)
          reject(err)
        })
    })
  }

  public [types.AUTH_AUTO_RENEW](context: ActionContext<IAuthState, IRootState>, timeout: number) {
    return new Promise<void>((resolve, reject) => {
      //On notifie la socket
      //@ts-ignore
      if (context.rootState.socket.isConnected) {
        context.dispatch(
          types.SOCKET_SENDMESSAGE,
          `${context.state.token.replace('Bearer ', 'rtk:')}.${
            document.cookie.match('(^|;)\\s*jwth\\s*=\\s*([^;]+)')?.pop() || ''
          }`,
        )
      }

      //si un renouvelement automatique est deja demandé alors on l'annule
      if (context.state.renewTokenId !== 0) {
        clearTimeout(context.state.renewTokenId)
      }
      //on recree une demande de renouvelement automatique
      const timoutId = setTimeout(() => {
        //le timeout a eu lieu on supprime l'id
        context.commit(types.AUTH_AUTO_RENEW, '')
        //genere un renew token
        context.dispatch(types.AUTH_USER_REQUEST)
      }, timeout)
      context.commit(types.AUTH_AUTO_RENEW, timoutId)
      resolve()
    })
  }

  public [types.AUTH_CHANGEPASSWORD_REQUEST](
    context: ActionContext<IAuthState, IRootState>,
    { oldPassword, newPassword }: { oldPassword: string; newPassword: string },
  ) {
    return new Promise((resolve, reject) => {
      context.commit(types.AUTH_CHANGEPASSWORD_REQUEST)
      apiAuth
        .changePassword(context.state.userProfile.id, oldPassword, newPassword)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            context.commit(types.AUTH_CHANGEPASSWORD_SUCCESS)
            context.dispatch(types.UI_ADD_SNACK_MESSAGE, i18n.t('ChangePassword.ChangePasswordSuccess'))
            resolve(true)
          } else {
            context.commit(types.AUTH_CHANGEPASSWORD_ERROR)
            reject((resp.data.data as AloError).errorMessage)
          }
        })
        .catch(err => {
          context.commit(types.AUTH_CHANGEPASSWORD_ERROR)
          reject(err)
        })
    })
  }

  public [types.AUTH_CHANGEPASSWORD_RESET_STATUS](context: ActionContext<IAuthState, IRootState>) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.AUTH_CHANGEPASSWORD_RESET_STATUS)
      resolve()
    })
  }

  public [types.AUTH_RESET_STATUS](context: ActionContext<IAuthState, IRootState>) {
    return new Promise<void>((resolve, reject) => {
      context.commit(types.AUTH_RESET_STATUS)
      resolve()
    })
  }

  public [types.AUTH_PASSWORD_REQUEST](context: ActionContext<IAuthState, IRootState>, data: AlqForgottenPass) {
    return new Promise((resolve, reject) => {
      context.commit(types.AUTH_CHANGEPASSWORD_REQUEST)
      apiAuth
        .forgetPassword(data)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            context.commit(types.AUTH_PASSWORD_SUCCESS)
            resolve(true)
          } else {
            context.commit(types.AUTH_PASSWORD_ERROR)
            reject((resp.data.data as AloError).errorMessage)
          }
        })
        .catch(err => {
          context.commit(types.AUTH_PASSWORD_ERROR)
          reject(err)
        })
    })
  }

  public [types.AUTH_CREATE_PASSWORD_REQUEST](context: ActionContext<IAuthState, IRootState>, data: AlqForgottenPass) {
    return new Promise((resolve, reject) => {
      context.commit(types.AUTH_CREATE_PASSWORD_REQUEST)
      apiAuth
        .createPassword(data)
        .then(resp => {
          if (resp.data.resultCode == ApiResultCode.Ok) {
            const identityToken = resp.data.data as AloIdentityToken
            const token = resp.headers.authorization
            //lance le renouvelement automatique
            context.dispatch(types.AUTH_AUTO_RENEW, (identityToken!.aloJwt.renewInSeconds - 10) * 1000)
            context.commit(types.AUTH_SUCCESS)
            context.commit(types.AUTH_USER_SUCCESS, {
              userProfile: identityToken!.aloUser,
              wsUrl: identityToken.wsUrl,
              token,
            })
            context.commit(types.AUTH_CREATE_PASSWORD_SUCCESS)
            loadInitalData().finally(() => {
              resolve(resp.data.data)
            })
          } else {
            context.commit(types.AUTH_LOGOUT)
            context.commit(
              types.AUTH_CREATE_PASSWORD_ERROR,
              i18n.t(`CreatePassword.${(resp.data.data as AloError).errorMessage}`),
            )
            reject(i18n.t(`CreatePassword.${(resp.data.data as AloError).errorMessage}`))
          }
        })
        .catch(err => {
          context.commit(types.AUTH_CREATE_PASSWORD_ERROR)
          reject(err)
        })
    })
  }
}

export default util.classToObject(AuthAction)
